import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

export type ConfirmOptions = {
  title?: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  /** When true (default for destructive flows), confirm button uses danger styling. */
  danger?: boolean
}

type OpenConfirmState = ConfirmOptions & { open: true }

const ConfirmContext = createContext<{
  confirm: (options: ConfirmOptions) => Promise<boolean>
} | null>(null)

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<OpenConfirmState | null>(null)
  const resolveRef = useRef<((value: boolean) => void) | null>(null)
  const confirmButtonRef = useRef<HTMLButtonElement>(null)

  const close = useCallback((result: boolean) => {
    resolveRef.current?.(result)
    resolveRef.current = null
    setState(null)
  }, [])

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve
      setState({ ...options, open: true })
    })
  }, [])

  useEffect(() => {
    if (!state?.open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(false)
    }
    window.addEventListener('keydown', onKeyDown)
    const focusTimer = window.setTimeout(() => confirmButtonRef.current?.focus(), 0)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.clearTimeout(focusTimer)
    }
  }, [state?.open, close])

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state?.open && (
        <div
          className="zimark-confirm-overlay"
          onClick={() => close(false)}
          role="presentation"
        >
          <div
            className="zimark-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="zimark-confirm-title"
            aria-describedby="zimark-confirm-message"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="zimark-confirm-title" className="zimark-confirm-title">
              {state.title || 'Please confirm'}
            </h2>
            <p id="zimark-confirm-message" className="zimark-confirm-message">
              {state.message.split('\n').map((line, index, lines) => (
                <React.Fragment key={`${index}-${line}`}>
                  {line}
                  {index < lines.length - 1 ? <br /> : null}
                </React.Fragment>
              ))}
            </p>
            <div className="zimark-confirm-actions">
              <button
                type="button"
                className="zimark-confirm-btn zimark-confirm-btn--cancel"
                onClick={() => close(false)}
              >
                {state.cancelLabel || 'Cancel'}
              </button>
              <button
                ref={confirmButtonRef}
                type="button"
                className={`zimark-confirm-btn zimark-confirm-btn--confirm${
                  state.danger !== false ? ' is-danger' : ''
                }`}
                onClick={() => close(true)}
              >
                {state.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider')
  }
  return ctx.confirm
}
